<!--
	@name common-dialog
	@description Display a dialog with headers and or footers
	@date 2020/03/04
	@license no license
	@copywrite Answers In Retirement Limited
-->

<!--
	@example

	<common-dialog ref="dialog" max-width="600" max-height="500" color="error">
		<template v-slot:header>Your Header</template>
		<template v-slot:body>
			<p>Some content</p>
		</template>
		<template v-slot:footer>Your Footer</template>
	</common-dialog>

	this.$refs.dialog.open();
-->

<template>
	<v-dialog v-model="active" scrollable :max-width="maxWidth" :max-height="maxHeight" :component="$options.name" :persistent="persistent" :eager="eager" @input="input">
		<v-card>
			<v-app-bar :dense="dense" :dark="dark" :color="color">
				<h2 class="header">
					<slot name="header" />
				</h2>
				<v-spacer />
				<slot v-show="!!$slots['header-action']" name="header-action" />
				<v-btn v-show="showClose" class="ga--trigger ga--dialog-closed" :data-ga-id="gaId" :data-ga-value="gaValue" :data-ga-test-id="gaTestId" icon @click="close">
					<v-icon>mdi-close-box</v-icon>
				</v-btn>
			</v-app-bar>
			<v-divider />
			<slot name="fixed" />
			<v-card-text :class="bodyClass">
				<slot name="body" />
			</v-card-text>
			<v-divider v-show="!!$slots['footer']" />
			<div v-show="!!$slots['footer']" class="footer">
				<slot name="footer" />
			</div>
		</v-card>
	</v-dialog>
</template>

<script>
	import { EventBus } from '@/utils';

	export default {
		name: 'common-dialog',

		props: {
			color: { type: String, default: 'blue-grey v-app-bar-generic-gradient' },
			dark: { type: Boolean, default: true },
			eager: { type: Boolean, default: false },
			persistent: { type: Boolean, default: false },
			dense: { type: Boolean, default: false },
			maxWidth: { type: Number, default: 900 },
			maxHeight: { type: Number, default: 500 },
			showClose: { type: Boolean, default: true },
			bodyClass: { type: String, default: '' },
			gaId: { type: String, default: null },
			gaValue: { type: String, default: null },
			gaTestId: { type: String, default: null }
		},

		data: () => ({
			active: false
		}),

		watch: {
			active(val) {
				if (!val) {
					this.$emit('closed', true);
					EventBus.$emit('common-dialog-closed');
				}
			}
		},

		methods: {
			/**
			 * @name open
			 * @description Open dialog and set properties
			 */
			open() {
				this.active = true;
				this.$emit('opened');
			},

			/**
			 * @name close
			 * @description Close dialog and set properties
			 */
			close() {
				this.active = false;
			},

			input() {
				// Google Analytics event
				window.dataLayer.push({
					event: 'dialogClosed',
					'gtm.element.dataset.gaId': this.gaId,
					'gtm.element.dataset.gaValue': this.gaValue,
					'gtm.element.dataset.gaTestId': this.gaTestId
				});
			}
		}
	};
</script>

<style scoped lang="scss">
	.v-app-bar {
		background-size: cover;
	}

	.header {
		font-size: 1.1rem;
		line-height: 1.5;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		font-weight: normal;
	}

	.footer {
		background-color: #f4f4f4;
	}
</style>
